import { ActionAfterViewed } from '@cian/action-after-viewed-component';
import * as React from 'react';

import { AllNewbuildingsBanner } from '../../components/NewbuildingBanners/AllNewbuildinsBanner/AllNewbuildingsBanner';
import { bannerTypeToDefaultTheme } from '../../components/NewbuildingBanners/utils/bannerTypeToTheme';
import {
  RedesignedNewbuildingRecommendationCard,
  RedesignedNewbuildingRecommendations as RedesignedNewbuildingRecommendationsView,
} from '../../components/RedesignedNewbuildingRecommendations';
import * as carouselStyles from '../../components/NewbuildingRecommendations/ItemsCarousel/ItemsCarousel.css';
import { ELoadingStatus } from '../../types/loadingStatus';
import { onAllOffersBannerClickAnalytic, onAllOffersBannerShowAnalytic } from './analytics/analytic';
import { Banner } from './Banner';
import { isNewbuildingBanner } from './helpers/isNewbuildingBanner';
import { mergeBannersAndRecommendations } from './helpers/mergeBannersAndRecommendations';
import { useNewbuildingRecommendations } from './useNewbuildingRecommendations';

type TRedesignedNewbuildingRecommendationsProps = {
  className?: string;
};

export const RedesignedNewbuildingRecommendations: React.FC<TRedesignedNewbuildingRecommendationsProps> = ({
  className,
}) => {
  const {
    newbuildings,
    banners,
    recommendationsStatus,
    onRecommendationsShow,
    onBannerShow,
    onBannerClick,
    onCardClick,
  } = useNewbuildingRecommendations();

  const bannersAndRecommendations = React.useMemo(
    () => mergeBannersAndRecommendations({ newbuildings, banners }),
    [banners, newbuildings],
  );

  const items = React.useMemo(
    () =>
      bannersAndRecommendations.map((item, index) => {
        const parentBlockCssClass =
          index > 4 ? carouselStyles['newbuilding-recommendations-banners-container'] : undefined;

        if (index === bannersAndRecommendations.length - 1) {
          return (
            <React.Fragment key={`last-item-${index}`}>
              {isNewbuildingBanner(item) && (
                <li>
                  <ActionAfterViewed
                    parentBlockCssClass={parentBlockCssClass}
                    callback={onBannerShow({ type: item.type })}
                  >
                    <Banner
                      onClick={onBannerClick({ type: item.type })}
                      data={item}
                      themeMapper={bannerTypeToDefaultTheme}
                    />
                  </ActionAfterViewed>
                </li>
              )}
              <li>
                <ActionAfterViewed parentBlockCssClass={parentBlockCssClass} callback={onAllOffersBannerShowAnalytic}>
                  <AllNewbuildingsBanner
                    onClick={onAllOffersBannerClickAnalytic}
                    url={newbuildings.allRecommendations.link}
                  />
                </ActionAfterViewed>
              </li>
            </React.Fragment>
          );
        }

        if (isNewbuildingBanner(item)) {
          return (
            <li key={`banner-${index}`}>
              <ActionAfterViewed parentBlockCssClass={parentBlockCssClass} callback={onBannerShow({ type: item.type })}>
                <Banner
                  onClick={onBannerClick({ type: item.type })}
                  data={item}
                  themeMapper={bannerTypeToDefaultTheme}
                />
              </ActionAfterViewed>
            </li>
          );
        }

        return (
          <li key={`recommendation-${item.id}`}>
            <RedesignedNewbuildingRecommendationCard
              recommendation={item}
              onClick={onCardClick({ newbuildingId: item.id, position: index })}
            />
          </li>
        );
      }),
    [bannersAndRecommendations, newbuildings.allRecommendations.link, onBannerClick, onBannerShow, onCardClick],
  );

  const renderItems = React.useCallback(() => items, [items]);

  if (recommendationsStatus !== ELoadingStatus.Success || !bannersAndRecommendations.length) {
    return null;
  }

  const content = (
    <ActionAfterViewed callback={onRecommendationsShow}>
      <RedesignedNewbuildingRecommendationsView renderItems={renderItems} />
    </ActionAfterViewed>
  );

  return className ? <div className={className}>{content}</div> : content;
};
